import { useQuery } from '@tanstack/react-query';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ResponsiveContainer, AreaChart, Area, YAxis, XAxis } from 'recharts';
import type { ReactNode } from 'react';
import { api } from '@/lib/api';
import { fmtMoneyCompact } from '@/lib/format';

type Props = {
  ticker: string;
  pnlPositive: boolean;
  children: ReactNode;
};

export function RowChartPreview({ ticker, pnlPositive, children }: Props) {
  return (
    <Tooltip.Provider delayDuration={250} skipDelayDuration={120}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="right"
            align="center"
            sideOffset={12}
            collisionPadding={16}
            className="z-50 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl p-3 w-72 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in"
          >
            <PreviewBody ticker={ticker} pnlPositive={pnlPositive} />
            <Tooltip.Arrow className="fill-zinc-800" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

function PreviewBody({
  ticker,
  pnlPositive,
}: {
  ticker: string;
  pnlPositive: boolean;
}) {
  const q = useQuery({
    queryKey: ['candles', ticker],
    queryFn: () => api.candles(ticker),
    staleTime: 5 * 60_000,
  });

  if (!q.data) {
    return (
      <div className="h-24 grid place-items-center text-[11px] text-zinc-400">
        Loading 90-day chart…
      </div>
    );
  }

  const data = q.data.points.map((p) => ({ t: p.t, c: p.c }));
  const stroke = pnlPositive ? '#34d399' : '#fb7185';
  const fill = pnlPositive ? 'url(#preview-g-pos)' : 'url(#preview-g-neg)';

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono text-[11px] text-zinc-300">
          {ticker.split('_')[0]}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-400">
          90 days
        </span>
      </div>
      <div className="h-24">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="preview-g-pos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="preview-g-neg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fb7185" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#fb7185" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="t" hide />
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Area
              type="monotone"
              dataKey="c"
              stroke={stroke}
              strokeWidth={1.5}
              fill={fill}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-400">
        <span>{fmtMoneyCompact(data[0]?.c ?? 0)}</span>
        <span>{fmtMoneyCompact(data[data.length - 1]?.c ?? 0)}</span>
      </div>
    </div>
  );
}
